/**
 * Converts keyboard, touch (swipe + tap zones), and pointer input into edge
 * triggered game intents. Simulation code consumes intents once per frame;
 * input collection stays decoupled from game state.
 */
export type IntentName = 'laneLeft' | 'laneRight' | 'confirm' | 'pause';

const SWIPE_THRESHOLD_PX = 32;
const SWIPE_MAX_MS = 500;

export class InputController {
  private readonly queue: IntentName[] = [];
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private touchActive = false;
  private swipeConsumed = false;

  private readonly onKeyDown = (event: KeyboardEvent) => {
    if (event.repeat) return;
    switch (event.code) {
      case 'KeyA':
      case 'ArrowLeft':
        this.queue.push('laneLeft');
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.queue.push('laneRight');
        break;
      case 'Enter':
      case 'Space':
      case 'KeyW':
      case 'ArrowUp':
      case 'KeyR':
        this.queue.push('confirm');
        break;
      case 'Escape':
      case 'KeyP':
        this.queue.push('pause');
        break;
      default:
        break;
    }
  };

  private readonly onPointerDown = (event: PointerEvent) => {
    // Ignore pointers that start on real UI buttons (pause, menu buttons).
    if ((event.target as HTMLElement).closest('button')) return;
    this.touchActive = true;
    this.swipeConsumed = false;
    this.touchStartX = event.clientX;
    this.touchStartY = event.clientY;
    // Event creation time, not handler run time: on slow frames the up handler
    // can run hundreds of ms late and must not turn a quick tap into a hold.
    this.touchStartTime = event.timeStamp;
  };

  private readonly onPointerMove = (event: PointerEvent) => {
    if (!this.touchActive || this.swipeConsumed) return;
    const dx = event.clientX - this.touchStartX;
    const dy = event.clientY - this.touchStartY;
    if (Math.abs(dx) >= SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
      this.queue.push(dx < 0 ? 'laneLeft' : 'laneRight');
      this.swipeConsumed = true;
    }
  };

  private readonly onPointerUp = (event: PointerEvent) => {
    if (!this.touchActive) return;
    this.touchActive = false;
    if (this.swipeConsumed) return;
    const elapsed = event.timeStamp - this.touchStartTime;
    const dx = event.clientX - this.touchStartX;
    const dy = event.clientY - this.touchStartY;
    if (elapsed <= SWIPE_MAX_MS && Math.hypot(dx, dy) < SWIPE_THRESHOLD_PX) {
      // A quick tap: on menus this confirms; while running it steers toward
      // the tapped half of the screen (mobile lane control).
      this.queue.push('confirm');
      this.queue.push(event.clientX < window.innerWidth / 2 ? 'laneLeft' : 'laneRight');
    }
  };

  private readonly onPointerCancel = () => {
    this.touchActive = false;
  };

  private readonly onBlur = () => {
    this.touchActive = false;
    this.queue.length = 0;
  };

  constructor(private readonly surface: HTMLElement) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('blur', this.onBlur);
    this.surface.addEventListener('pointerdown', this.onPointerDown);
    this.surface.addEventListener('pointermove', this.onPointerMove);
    this.surface.addEventListener('pointerup', this.onPointerUp);
    this.surface.addEventListener('pointercancel', this.onPointerCancel);
  }

  /** Drain queued intents; called once per frame by the game. */
  consume(handler: (intent: IntentName) => void): void {
    for (const intent of this.queue) handler(intent);
    this.queue.length = 0;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('blur', this.onBlur);
    this.surface.removeEventListener('pointerdown', this.onPointerDown);
    this.surface.removeEventListener('pointermove', this.onPointerMove);
    this.surface.removeEventListener('pointerup', this.onPointerUp);
    this.surface.removeEventListener('pointercancel', this.onPointerCancel);
  }
}
